import React, { useState } from "react";
import { useSnackbar } from "notistack";
import { Button, DialogActions, DialogContent, DialogTitle, Grid, FormControl, LinearProgress, Typography, TextField, Dialog, Menu, MenuItem } from "@material-ui/core";
import { getAggregatedForms, exportAggregatedForms, AggregatedSipForms, exportFormsList } from "api/admin/sip";
import { ClubSelect, DateSelect } from "components";
import i18n from "./i18n";

// prop types
interface SIPAggregateDialogProps {
    show: boolean,
    onClose: () => void,
}

// age groups
const ageGroups = ["kids", "youngAdults", "adults", "seniors", "unknownAge", "disabled"] as const;

// genders
const genders = ["m", "f", "d", "u"] as const;

// weather conditions
const weatherConditions = ["sunny", "cloudy", "veryCloudy", "rainy", "veryRainy"] as const;

// edit SIP aggregate & export dialog
const SIPAggregateDialog = ({ show, onClose }: SIPAggregateDialogProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [showLoadingAnimation, setShowLoadingAnimation] = useState(false);
    const [filterQuery, setFilterQuery] = useState<Parameters<typeof getAggregatedForms>[0]>({});
    const [sipData, setSIPData] = useState<AggregatedSipForms>();
    const [showExportMenu, setShowExportMenu] = useState<HTMLButtonElement | null>(null);

    // fetch aggregated sip data
    const fetchData = async () => {
        setShowLoadingAnimation(true);
        try {
            setSIPData(await getAggregatedForms(filterQuery));
        } catch (error) {
            displayError(error);
        }
        setShowLoadingAnimation(false);
    };

    // export aggregated sip form data
    const exportData = async (aggregated: boolean) => {
        setShowExportMenu(null);
        setShowLoadingAnimation(true);
        try {
            const { url } = aggregated ? await exportAggregatedForms(filterQuery) : await exportFormsList(filterQuery);
            window.open(url);
        } catch (error) {
            displayError(error);
        }
        setShowLoadingAnimation(false);
    };

    // clear data if filter query changes
    React.useEffect(() => setSIPData(undefined), [filterQuery]);

    // render meta row
    const renderMetaRow = (label: string, content: React.ReactNode) => (
        <React.Fragment key={label}>
            <Grid item xs={4}>
                <Typography>{label}</Typography>
            </Grid>
            <Grid item xs={8}>
                {content}
            </Grid>
        </React.Fragment>
    );

    // render participants header
    const renderParticipantsHeader = () => (
        <Grid container alignItems="center" spacing={1}>
            <Grid item xs={4} />
            {genders.map((gender) => (
                <Grid item xs={2} key={gender}>
                    <Typography>{i18n.genders[gender]}</Typography>
                </Grid>
            ))}
        </Grid>
    );

    // render participants row
    const renderParticipantsRow = (ageGroup: typeof ageGroups[number]) => (
        <Grid container alignItems="center" spacing={1} key={ageGroup}>
            <Grid item xs={4}>
                <Typography>{i18n.ageGroups[ageGroup]}</Typography>
            </Grid>
            {genders.map((gender) => (
                <Grid item xs={2} key={gender}>
                    <Typography>{sipData?.aggregatedFormData[ageGroup][gender]}</Typography>
                </Grid>
            ))}
        </Grid>
    );

    // render
    return (
        <>
            {/* main dialog */}
            <Dialog
                fullWidth
                maxWidth="lg"
                open={show}
                onClose={onClose}
            >
                <DialogTitle>{i18n.sipAggregateLong}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>

                        {/* club */}
                        <Grid item xs={12}>
                            <ClubSelect
                                clubFilter={(club) => club.capabilities.includes("sip")}
                                clubId={filterQuery.clubId}
                                controlProps={{ fullWidth: true }}
                                userRoles={["owner", "admin", "trainer"]}
                                onChange={(clubId) => setFilterQuery({ ...filterQuery, clubId })}
                            />
                        </Grid>

                        {/* bookings from */}
                        <Grid item xs={6}>
                            <DateSelect
                                label={i18n.bookingsFrom}
                                value={filterQuery.fromDate}
                                onChange={(m) => setFilterQuery({ ...filterQuery, fromDate: m?.startOf("day").toISOString() })}
                            />
                        </Grid>

                        {/* bookings to */}
                        <Grid item xs={6}>
                            <DateSelect
                                label={i18n.bookingsTo}
                                value={filterQuery.toDate}
                                onChange={(m) => setFilterQuery({ ...filterQuery, toDate: m?.endOf("day").toISOString() })}
                            />
                        </Grid>

                        {/* booking query */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={i18n.bookingQuery}
                                value={filterQuery.bookingQuery || ""}
                                onChange={(e) => setFilterQuery({ ...filterQuery, bookingQuery: e.target.value })}
                            />
                        </Grid>

                        {/* data preview */}
                        {sipData && (
                            <Grid item xs={12}>
                                {/* meta */}
                                <FormControl fullWidth margin="normal">
                                    <Typography variant="subtitle2" gutterBottom>{i18n.general}</Typography>
                                    <Grid container spacing={1}>
                                        {renderMetaRow(i18n.numPrefilledForms, (
                                            <Typography>{sipData.numPrefilledForms}</Typography>
                                        ))}
                                        {renderMetaRow(i18n.numManualForms, (
                                            <Typography>{sipData.numManualForms}</Typography>
                                        ))}
                                    </Grid>
                                </FormControl>

                                {/* weather conditions */}
                                <FormControl fullWidth margin="normal">
                                    <Typography variant="subtitle2" gutterBottom>{i18n.weatherConditions}</Typography>
                                    <Grid container spacing={1}>
                                        {weatherConditions.map((c) => renderMetaRow(i18n.weatherConditionsList[c], (
                                            <Typography>{sipData?.weatherConditions[c]}</Typography>
                                        )))}
                                    </Grid>
                                </FormControl>

                                {/* participants */}
                                <FormControl fullWidth margin="normal">
                                    <Typography variant="subtitle2" gutterBottom>{i18n.participantsPerAgeGroup}</Typography>
                                    {renderParticipantsHeader()}
                                    {ageGroups.map(renderParticipantsRow)}
                                </FormControl>
                            </Grid>
                        )}

                    </Grid>
                </DialogContent>
                {showLoadingAnimation && (
                    <LinearProgress />
                )}
                <DialogActions>
                    <Button onClick={(e) => setShowExportMenu(e.currentTarget)} color="primary" style={{ marginRight: "auto" }} disabled={!sipData}>
                        {i18n.export}
                    </Button>
                    <Button onClick={onClose} color="secondary">
                        {i18n.cancel}
                    </Button>
                    <Button onClick={fetchData} color="primary" disabled={showLoadingAnimation}>
                        {i18n.create}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* export menu */}
            <Menu
                anchorEl={showExportMenu}
                anchorOrigin={{ vertical: "center", horizontal: "center" }}
                getContentAnchorEl={null}
                open={!!showExportMenu}
                onClose={() => setShowExportMenu(null)}
            >
                <MenuItem disabled={showLoadingAnimation} onClick={() => exportData(true)}>{i18n.aggregated}</MenuItem>
                <MenuItem disabled={showLoadingAnimation} onClick={() => exportData(false)}>{i18n.single}</MenuItem>
            </Menu>
        </>
    );
};

export default SIPAggregateDialog;
